import { Component, OnInit, OnDestroy } from '@angular/core';
import { AsyncPipe, NgClass, NgOptimizedImage } from '@angular/common';
import { Router, RouterLink, RouterLinkActive } from '@angular/router';
import {
  BookUser,
  Calendar, Contact,
  CalendarClock,
  Ellipsis,
  BookDashed,
  Flame,
  LayoutDashboard,
  LucideAngularModule,
  Map,
  Package,
  SquareKanban,
  User,
  FileText,
  Banknote,
  CalendarCheck,
  Mail
} from 'lucide-angular';
import { SidebarStateService } from './sidebar-state.service';
import { AuthService } from '@app/auth/auth.service';
import { assetUrl } from '@app/shared/utils/asset-url.util';
import { NewQuotesBadgeService } from '@app/board/shared/new-quotes-badge.service';
import { FeatureToggleService } from '@app/feature-toggles/feature-toggle.service';
import { combineLatest, Observable, of } from 'rxjs';

interface SidebarItem {
  title: string;
  icon: any;
  route: string;
  badgeKey?: string;
  // TVG-147: when set, the item is only shown if this feature toggle is enabled.
  featureToggle?: string;
}

@Component({
  selector: 'app-sidebar',
  imports: [NgClass, RouterLink, NgOptimizedImage, LucideAngularModule, RouterLinkActive, AsyncPipe],
  templateUrl: './sidebar.component.html',
  styleUrl: './sidebar.component.css',
  standalone: true,
})
export class SidebarComponent implements OnInit, OnDestroy {
  protected expanded?: boolean;
  protected selected?: string;
  protected page?: string;
  protected readonly EllipsisIcon = Ellipsis;
  protected readonly logoUrl = assetUrl('images/the-venue-group-logo.svg');

  // TVG-117: badge stream powering the "Deals" nav entry. Zero / hidden when
  // crm-sales-flow is off.
  protected newQuotesCount$: Observable<number> = of(0);

  protected mainItems: SidebarItem[] = [
    // TVG-300: labelled "Deals" — the page manages deals, in HubSpot's sense.
    // The /board route, component and API path keep their existing names so
    // saved links and bookmarks still resolve.
    { title: 'Deals', icon: SquareKanban, route: '/board', badgeKey: 'new-quotes' },
    // TVG-363: Contacts moved here, directly under Deals, from the Manage group.
    { title: 'Contacts', icon: Contact, route: '/contacts' },
    // TVG-236: Sales Dashboard. Only shown when the `sales-dashboard` toggle is on.
    { title: 'Sales Dashboard', icon: LayoutDashboard, route: '/sales-dashboard', featureToggle: 'sales-dashboard' },
    // TVG-147: Sales AI POC. Only shown when the `sales-ai` feature toggle is on.
    { title: 'AI Sales Assistant', icon: Flame, route: '/ai-sales-assistant', featureToggle: 'sales-ai' },
    // TVG-150: AI Events Assistant. Same `sales-ai` toggle gates the events-side hub.
    { title: 'AI Events Assistant', icon: CalendarClock, route: '/ai-events-assistant', featureToggle: 'sales-ai' },
    // TVG-176: Live payment remediation page. Only shown when the
    // `payment-remediation` feature toggle is on.
    { title: 'Payment Remediation', icon: Banknote, route: '/payment-remediation', featureToggle: 'payment-remediation' },
    // TVG-337: Kate's view of the viewings as a schedule. Rides the same
    // `crm-sales-flow` toggle as the rest of the show-round stack, so the entry
    // is absent until that is on.
    { title: 'Viewings', icon: CalendarCheck, route: '/show-rounds', featureToggle: 'crm-sales-flow' },
    // TVG-343: the inbox-and-sent-box in one place. Behind the `communications`
    // toggle, so with it off staff see no entry and no page.
    { title: 'Communications', icon: Mail, route: '/communications', featureToggle: 'communications' },
    { title: 'Events', icon: BookUser, route: '/events' },
    { title: 'Calendar', icon: Calendar, route: '/events-calendar' },
    { title: 'Venues', icon: Map, route: '/venues' },
    { title: 'Quotes', icon: FileText, route: '/quotes' },
  ];

  protected manageItems: SidebarItem[] = [
    { title: 'Event Templates', icon: BookDashed, route: '/event-templates' },
    { title: 'Products', icon: Package, route: '/products' },
    { title: 'Users', icon: User, route: '/users' },
  ];

  filteredMainItems: SidebarItem[] = [];
  filteredManageItems: SidebarItem[] = [];

  constructor(
    private sidebarStateService: SidebarStateService,
    private authService: AuthService,
    private router: Router,
    private newQuotesBadgeService: NewQuotesBadgeService,
    private featureToggleService: FeatureToggleService,
  ) {}

  ngOnInit(): void {
    this.expanded = this.sidebarStateService.isExpanded();
    this.sidebarStateService.stateChange.subscribe((state: boolean) => {
      this.expanded = state;
    });

    this.newQuotesCount$ = this.newQuotesBadgeService.count$;

    // TVG-236: use combineLatest so filtering waits for both the user AND the
    // feature-toggle state to be resolved. Using getUser() alone caused a race
    // condition: if the user was already cached, filterItemsByRoles() ran before
    // loadActive() completed, leaving the toggle state as {} and hiding every
    // toggle-gated sidebar entry (e.g. Sales Dashboard) permanently.
    combineLatest([
      this.authService.getUser(),
      this.featureToggleService.loadActive(),
    ]).subscribe(([user, _toggleState]) => {
      const userRoles = user?.roles || [];
      const routes = this.router.config;

      this.filteredMainItems = this.filterItemsByRoles(this.mainItems, routes, userRoles);
      this.filteredManageItems = this.filterItemsByRoles(this.manageItems, routes, userRoles);

      // TVG-117: only authenticated users with Board access ever see the badge.
      if (this.filteredMainItems.some((item) => item.route === '/board')) {
        this.newQuotesBadgeService.refresh();
      }
    });

    // Auto-close sidebar on window resize to prevent content hiding
    this.handleWindowResize();
  }

  private filterItemsByRoles(
    items: SidebarItem[],
    routes: any[],
    userRoles: string[],
  ): SidebarItem[] {
    const findRouteRoles = (routePath: string, routes: any[]): string[] => {
      for (const route of routes) {
        if (route.path === routePath.replace('/', '')) {
          return route.data?.roles || [];
        }
        if (route.children) {
          const roles = findRouteRoles(routePath, route.children);
          if (roles.length) {
            return roles;
          }
        }
      }
      return [];
    };

    return items.filter((item) => {
      // TVG-147: hide toggle-gated items when their feature toggle is off.
      if (item.featureToggle && !this.featureToggleService.isEnabled(item.featureToggle)) {
        return false;
      }
      const roles = findRouteRoles(item.route, routes);
      return roles.includes('*') || roles.some((role: string) => userRoles.includes(role));
    });
  }

  toggleSidebar(): void {
    this.sidebarStateService.toggleSidebar();
  }

  closeSidebar(): void {
    if (this.expanded) {
      this.sidebarStateService.toggleSidebar();
    }
  }

  onNavigate(): void {
    // Close sidebar on mobile after navigation
    if (this.expanded && window.innerWidth < 1024) {
      this.sidebarStateService.toggleSidebar();
    }
  }

  private resizeHandler?: () => void;

  ngOnDestroy(): void {
    if (this.resizeHandler) {
      window.removeEventListener('resize', this.resizeHandler);
    }
  }

  private handleWindowResize(): void {
    let previousWidth = window.innerWidth;

    this.resizeHandler = () => {
      const currentWidth = window.innerWidth;
      const wasDesktop = previousWidth >= 1024; // lg breakpoint
      const isNowMobile = currentWidth < 1024;

      // If transitioning from desktop to mobile and sidebar is expanded, close it
      if (wasDesktop && isNowMobile && this.expanded) {
        this.sidebarStateService.toggleSidebar();
      }

      previousWidth = currentWidth;
    };

    window.addEventListener('resize', this.resizeHandler);
  }

}
